/* SBO v3 header behaviour: mega menu, mobile drawer, guided predictive
   search, locale switcher, sticky-on-scroll. Loaded once by
   layout/theme.liquid as a deferred script -- deferred scripts run after
   the full document has parsed, so window.routes and window.sboLocale
   (both set by an earlier inline script near the end of <body>) are always
   already populated by the time this file executes.
   There is exactly one header per real page, so every lookup below is a
   plain [data-sbo3-*] attribute query rather than an id generated per
   section instance -- a static file cannot contain a Liquid-rendered id.
   Edit here, not in the section. */

  (() => {
    const root      = document.querySelector('[data-sbo3-header]');
    const drawer    = document.querySelector('[data-sbo3-drawer]');
    const overlay   = document.querySelector('[data-sbo3-overlay]');
    const burger    = root && root.querySelector('[data-sbo3-burger]');
    const closeBtn  = drawer && drawer.querySelector('[data-sbo3-close]');
    const searchPanel = document.querySelector('[data-sbo3-search-panel]');
    const searchInput = root && root.querySelector('[data-sbo3-primary-search]');
    if (!root) return;

    /* Keep the selected concern explicit across Shopify collection URL variants. */
    const concernNav = root.querySelector('.sbov3header__concern-nav');
    if (concernNav) {
      const concernLinks = Array.from(concernNav.querySelectorAll('a'));
      const normalizedUrl = value => {
        const url = new URL(value, window.location.origin);
        const path = url.pathname.replace(/\/$/, '').toLowerCase();
        const params = Array.from(url.searchParams.entries()).sort(([aKey, aValue], [bKey, bValue]) => {
          const keyOrder = aKey.localeCompare(bKey);
          return keyOrder || aValue.localeCompare(bValue);
        });
        const query = new URLSearchParams(params).toString();
        return query ? `${path}?${query}` : path;
      };
      const normalizedPath = value => new URL(value, window.location.origin).pathname.replace(/\/$/, '').toLowerCase();
      const currentUrl = normalizedUrl(window.location.href);
      const currentPath = normalizedPath(window.location.href);
      const setActiveConcern = activeLink => {
        concernLinks.forEach(link => {
          const isActive = link === activeLink;
          link.classList.toggle('is-active', isActive);
          if (isActive) link.setAttribute('aria-current', 'page');
          else link.removeAttribute('aria-current');
        });
      };

      /* Remembered selection.
         A concern link and the page it actually lands on are not always the same
         URL: Shopify URL redirects, and collections whose handle differs from the
         configured link, both break a pure path comparison. When that happens the
         shopper's choice must still read as selected, so the click is remembered
         and the real landing path is learned from it for next time. */
      const STORE_KEY = 'sbo3HeaderConcern';
      const readStore = () => {
        try {
          const raw = sessionStorage.getItem(STORE_KEY);
          if (!raw) return {};
          if (raw.charAt(0) !== '{') return { label: raw };  // pre-JSON value
          return JSON.parse(raw) || {};
        } catch { return {}; }
      };
      const writeStore = value => {
        try { sessionStorage.setItem(STORE_KEY, JSON.stringify(value)); } catch {}
      };

      const stored = readStore();
      const byLabel = label => concernLinks.find(link => link.textContent.trim() === label);

      const exactMatch = concernLinks.find(link => normalizedUrl(link.href) === currentUrl);
      const pathMatch = concernLinks.find(link => normalizedPath(link.href) === currentPath);
      const liquidActive = concernLinks.find(link => link.classList.contains('is-active'));

      let activeLink = exactMatch || pathMatch || liquidActive || null;

      if (activeLink) {
        // Authoritative: record which path this concern really resolves to.
        writeStore({ label: activeLink.textContent.trim(), path: currentPath });
      } else if (stored.pending && currentPath && byLabel(stored.label)) {
        // The click that caused this navigation; the destination redirected.
        activeLink = byLabel(stored.label);
        writeStore({ label: stored.label, path: currentPath });
      } else if (stored.path && stored.path === currentPath && byLabel(stored.label)) {
        // Same page again (reload, back button) — reuse the learned mapping.
        activeLink = byLabel(stored.label);
      }

      setActiveConcern(activeLink);

      /* Select on pointerdown, not click.
         A class added in a click handler usually never paints — the browser has
         already committed the navigation — so the shopper taps a concern and
         sees nothing happen until the next page renders. pointerdown fires
         before navigation starts, so the selection is visible during the load. */
      const selectConcern = link => {
        if (link.classList.contains('is-active')) return;
        setActiveConcern(link);
      };

      concernLinks.forEach(link => {
        link.addEventListener('pointerdown', () => selectConcern(link));
        link.addEventListener('keydown', event => {
          if (event.key === 'Enter' || event.key === ' ') selectConcern(link);
        });
        link.addEventListener('click', () => {
          selectConcern(link);
          writeStore({ label: link.textContent.trim(), pending: true });
        });
      });
    }

    /* Guided predictive search */
    if (searchPanel && searchInput) {
      const searchResults = searchPanel.querySelector('[data-sbo3-search-results]');
      let searchTimer;
      let searchRequest;
      let currentSearchProducts = [];
      let activeSearchIndex = 0;

      const escapeHtml = value => String(value || '').replace(/[&<>'"]/g, char => ({
        '&': '&amp;', '<': '&lt;', '>': '&gt;', "'": '&#39;', '"': '&quot;'
      }[char]));

      function openSearchResults() {
        searchPanel.hidden = false;
        searchInput.setAttribute('aria-expanded', 'true');
      }
      function closeSearchResults() {
        searchPanel.hidden = true;
        searchInput.setAttribute('aria-expanded', 'false');
      }

      const stripHtml = value => {
        const documentFragment = new DOMParser().parseFromString(String(value || ''), 'text/html');
        return (documentFragment.body.textContent || '').replace(/\s+/g, ' ').trim();
      };

      /* An <a>, not a <button>: on the wide layout below, tapping still just
         updates the .sbov3search__preview panel via the click listener
         further down (no preventDefault there, so navigation is not
         suppressed) -- unchanged from before. On the narrow layout, where
         that preview panel is hidden entirely (see the max-width: 900px
         block in the header part of sbo-v3.css), this href is what makes a tap actually
         go anywhere; a <button> only ever drove the now-hidden panel and
         would have been a dead tap on mobile. */
      function productRow(product, index) {
        const imageUrl = typeof product.image === 'string' ? product.image : product.image && product.image.url;
        const image = imageUrl
          ? `<img src="${escapeHtml(imageUrl)}" alt="" width="56" height="56" loading="lazy">`
          : '<span aria-hidden="true"></span>';
        return `<a class="sbov3search__result${index === 0 ? ' is-active' : ''}" href="${escapeHtml(product.url)}" data-search-product="${index}"${index === 0 ? ' aria-current="true"' : ''}>
          ${image}
          <span class="sbov3search__result-copy"><small>${escapeHtml(product.vendor || 'Product')}</small><span>${escapeHtml(product.title)}</span></span>
          <span class="sbov3search__result-price">${escapeHtml(product.price || '')}</span>
        </a>`;
      }

      function linkRow(item, label) {
        return `<a class="sbov3search__result" href="${escapeHtml(item.url)}">
          <span aria-hidden="true"></span>
          <span class="sbov3search__result-copy"><small>${escapeHtml(label)}</small><span>${escapeHtml(item.title)}</span></span>
        </a>`;
      }

      function resultGroup(title, items, renderer, query) {
        if (!items || !items.length) return '';
        return `<section class="sbov3search__result-group"><div class="sbov3search__result-head"><strong>${title}</strong><a href="${window.routes.search_url}?q=${encodeURIComponent(query)}">View all</a></div><div class="sbov3search__result-list">${items.map(renderer).join('')}</div></section>`;
      }

      function guidanceFor(product) {
        const tagList = Array.isArray(product.tags) ? product.tags : String(product.tags || '').split(',');
        const taggedValue = (...keys) => {
          const match = tagList.find(tag => keys.some(key => new RegExp(`^\\s*${key}\\s*:`, 'i').test(tag)));
          return match ? match.split(':').slice(1).join(':').trim() : '';
        };
        const keyIngredient = taggedValue('key ingredient', 'ingredient');
        const ingredientNote = taggedValue('ingredient benefit');
        const step = taggedValue('routine step');
        const time = taggedValue('time of day');
        const routineNote = taggedValue('routine compatibility');
        const body = stripHtml(product.body || '');
        const why = body ? `${body.slice(0, 160)}${body.length > 160 ? '…' : ''}` : '';
        return { keyIngredient, ingredientNote, step, time, routineNote, why };
      }

      function renderSearchPreview(index) {
        const product = currentSearchProducts[index];
        const target = searchResults.querySelector('[data-sbo3-search-preview]');
        if (!product || !target) return;
        activeSearchIndex = index;
        searchResults.querySelectorAll('[data-search-product]').forEach(row => {
          const active = Number(row.dataset.searchProduct) === index;
          row.classList.toggle('is-active', active);
          if (active) row.setAttribute('aria-current', 'true');
          else row.removeAttribute('aria-current');
        });
        const imageUrl = typeof product.image === 'string' ? product.image : product.image && product.image.url;
        const guidance = guidanceFor(product);
        const chips = [guidance.step, guidance.time].filter(Boolean);
        const guidanceSections = [
          guidance.why ? `<section><h4>Why it may suit you</h4><p>${escapeHtml(guidance.why)}</p></section>` : '',
          guidance.keyIngredient ? `<section><h4>Ingredient details</h4><p><strong>${escapeHtml(guidance.keyIngredient)}</strong>${guidance.ingredientNote ? `<br>${escapeHtml(guidance.ingredientNote)}` : ''}</p></section>` : '',
          (guidance.step || guidance.time || guidance.routineNote) ? `<section><h4>How it fits your routine</h4><p>${escapeHtml([guidance.step ? `${guidance.step} step` : '', guidance.time].filter(Boolean).join(' · '))}${guidance.routineNote ? `${guidance.step || guidance.time ? '. ' : ''}${escapeHtml(guidance.routineNote)}` : ''}</p><a href="${escapeHtml(product.url)}">See complete product details</a></section>` : ''
        ].filter(Boolean).join('');
        const variants = Array.isArray(product.__variants) ? product.__variants : null;
        const availableVariants = variants ? variants.filter(variant => variant.available) : [];
        const preorderNote = isPreorder(product) ? root.dataset.preorderNote : '';
        const addLabel = preorderNote ? (root.dataset.preorderLabel || 'Pre-order') : 'Add to cart';
        const primaryAction = !variants
          ? ''
          : availableVariants.length === 0
            ? '<button type="button" data-sbo3-card-action="primary" disabled>Sold out</button>'
            : variants.length === 1
              ? `<button type="button" data-sbo3-card-action="primary" data-search-add-cart data-variant-id="${escapeHtml(availableVariants[0].id)}"${preorderNote ? ` data-preorder-note="${escapeHtml(preorderNote)}"` : ''}>${escapeHtml(addLabel)}</button>`
              : `<a class="sbov3search__preview-primary" data-sbo3-card-action="primary" href="${escapeHtml(product.url)}">Choose options</a>`;
        target.innerHTML = `<div class="sbov3search__preview-hero">
          <a class="sbov3search__preview-image" data-sbo3-card-media href="${escapeHtml(product.url)}">${imageUrl ? `<img src="${escapeHtml(imageUrl)}" alt="${escapeHtml(product.title)}">` : ''}</a>
          <div class="sbov3search__preview-copy">
            <p class="sbov3search__preview-vendor">${escapeHtml(product.vendor || 'Medical-grade skincare')}</p>
            <h3>${escapeHtml(product.title)}</h3>
            ${chips.length ? `<div class="sbov3search__preview-chips">${chips.map(chip => `<span>${escapeHtml(chip)}</span>`).join('')}</div>` : ''}
            <p class="sbov3search__preview-price">${escapeHtml(product.price || '')}</p>
          </div>
        </div>
        ${guidanceSections ? `<div class="sbov3search__preview-guidance">${guidanceSections}</div>` : ''}
        <div class="sbov3search__preview-actions">${primaryAction}<a href="${escapeHtml(product.url)}">View product</a></div>`;

        if (!product.__detailsRequested) {
          product.__detailsRequested = true;
          const productUrl = product.url.split('?')[0].replace(/\/$/, '');
          fetch(`${productUrl}.js`, { headers: { Accept: 'application/json' } })
            .then(response => response.ok ? response.json() : Promise.reject(new Error('Product unavailable')))
            .then(details => {
              product.body = details.description || product.body;
              product.tags = details.tags || product.tags;
              product.__variants = details.variants || [];
              if (activeSearchIndex === index) renderSearchPreview(index);
            })
            .catch(() => {});
        }
      }

      /* Pre-order products (Theme settings > Pre-order, see
         snippets/sbo-preorder.liquid): the header lists their ids, and the
         add button puts the shipping note on the cart line. */
      function isPreorder(product) {
        const ids = (root && root.dataset.preorderIds || '').split(',').filter(Boolean);
        return ids.includes(String(product.id));
      }

      async function addSearchProduct(button) {
        if (button.disabled) return;
        const original = button.textContent;
        button.disabled = true;
        button.setAttribute('aria-busy', 'true');
        button.textContent = 'Adding…';
        try {
          const line = { id: Number(button.dataset.variantId), quantity: 1 };
          if (button.dataset.preorderNote) line.properties = { 'Pre-order': button.dataset.preorderNote };
          const { response: cartResponse, data: payload } = await window.SBOCartEvents.requestWithTimeout('/cart/add.js', { method: 'POST', headers: { 'Content-Type': 'application/json', Accept: 'application/json' }, body: JSON.stringify({ items: [line] }) });
          if (!cartResponse.ok) throw new Error(payload.description || 'Unable to add this product.');
          button.textContent = 'Added to cart';
          if (window.SBOCartEvents?.afterMutation) {
            await window.SBOCartEvents.afterMutation({ source: 'header-search', item: payload });
          } else {
            document.dispatchEvent(new CustomEvent('cart:updated', { detail: { action: 'add', item: payload } }));
          }
          setTimeout(() => { button.disabled = false; button.removeAttribute('aria-busy'); button.textContent = original; }, 1800);
        } catch (error) {
          if (error.name === 'AbortError') {
            const cartData = await window.SBOCartEvents?.afterMutation?.({ source: 'header-search-timeout', action: 'reconcile' });
            const reconciled = Boolean(cartData);
            button.textContent = reconciled ? 'Check cart' : 'Refresh page';
            window.SBOCartNotice?.show({
              error: !reconciled,
              message: reconciled
                ? 'The response was slow, so we refreshed your cart. Check the quantity before adding again.'
                : 'We could not verify your cart. Refresh the page before trying again.'
            });
            if (reconciled) {
              setTimeout(() => {
                button.disabled = false;
                button.removeAttribute('aria-busy');
                button.textContent = original;
              }, 2400);
            }
            return;
          }
          button.disabled = false;
          button.removeAttribute('aria-busy');
          button.textContent = error.name === 'AbortError' ? 'Connection timed out' : 'Try again';
          const message = error.name === 'AbortError' ? 'The cart took too long to respond. Please try again.' : error.message;
          window.SBOCartNotice?.show({ error: true, message });
          setTimeout(() => { button.textContent = original; }, 1800);
        }
      }

      async function runSearch(query) {
        if (searchRequest) searchRequest.abort();
        searchRequest = new AbortController();
        openSearchResults();
        searchResults.hidden = false;
        searchResults.innerHTML = '<p class="sbov3search__message">Finding the best matches…</p>';
        try {
          const endpoint = `${window.routes.predictive_search_url}?q=${encodeURIComponent(query)}&resources[type]=product,collection,article&resources[limit]=6&resources[options][unavailable_products]=last`;
          const response = await fetch(endpoint, { signal: searchRequest.signal, headers: { Accept: 'application/json' } });
          if (!response.ok) throw new Error('Search request failed');
          const payload = await response.json();
          const matches = payload.resources && payload.resources.results ? payload.resources.results : {};
          currentSearchProducts = matches.products || [];
          const resultMarkup = [
            resultGroup('Recommended products', matches.products, productRow, query),
            resultGroup('Routines and concerns', matches.collections, item => linkRow(item, 'Collection'), query),
            resultGroup('Skincare guidance', matches.articles, item => linkRow(item, 'Guide'), query)
          ].join('');
          searchResults.innerHTML = resultMarkup
            ? currentSearchProducts.length
              ? `<div class="sbov3search__workspace"><div class="sbov3search__result-column">${resultMarkup}</div><aside class="sbov3search__preview" data-sbo3-search-preview data-sbo3-product-card data-sbo3-card-kind="preview" data-card-mode="guided" aria-label="Selected product preview"></aside></div>`
              : `<div class="sbov3search__workspace is-list-only"><div class="sbov3search__result-column">${resultMarkup}</div></div>`
            : `<p class="sbov3search__message">No close matches yet. Try a concern such as “dark spots,” an ingredient, or a brand name.</p>`;
          if (currentSearchProducts.length) renderSearchPreview(0);
        } catch (error) {
          if (error.name !== 'AbortError') searchResults.innerHTML = '<p class="sbov3search__message">We could not load suggestions. Press Enter to view full results.</p>';
        }
      }

      searchResults.addEventListener('mouseover', event => {
        const row = event.target.closest('[data-search-product]');
        if (row) renderSearchPreview(Number(row.dataset.searchProduct));
      });
      searchResults.addEventListener('focusin', event => {
        const row = event.target.closest('[data-search-product]');
        if (row) renderSearchPreview(Number(row.dataset.searchProduct));
      });
      searchResults.addEventListener('click', event => {
        const row = event.target.closest('[data-search-product]');
        if (row) renderSearchPreview(Number(row.dataset.searchProduct));
        const addButton = event.target.closest('[data-search-add-cart]');
        if (addButton) addSearchProduct(addButton);
      });

      searchInput.addEventListener('input', () => {
        const query = searchInput.value.trim();
        clearTimeout(searchTimer);
        if (query.length < 2) {
          searchResults.hidden = true;
          searchResults.innerHTML = '';
          closeSearchResults();
          return;
        }
        searchTimer = setTimeout(() => runSearch(query), 180);
      });
      searchInput.addEventListener('focus', () => {
        const query = searchInput.value.trim();
        if (query.length > 1) runSearch(query);
      });

      /* Keyboard: ArrowDown from the field enters the results; arrows move between rows. */
      const focusableResults = () => Array.from(searchResults.querySelectorAll('.sbov3search__result'));
      searchInput.addEventListener('keydown', event => {
        if (event.key !== 'ArrowDown' || searchPanel.hidden) return;
        const rows = focusableResults();
        if (!rows.length) return;
        event.preventDefault();
        rows[0].focus();
      });
      searchResults.addEventListener('keydown', event => {
        if (event.key !== 'ArrowDown' && event.key !== 'ArrowUp') return;
        const rows = focusableResults();
        const position = rows.indexOf(document.activeElement);
        if (position === -1) return;
        event.preventDefault();
        if (event.key === 'ArrowUp' && position === 0) {
          searchInput.focus();
          return;
        }
        const next = rows[position + (event.key === 'ArrowDown' ? 1 : -1)];
        if (next) next.focus();
      });

      document.addEventListener('keydown', event => {
        if (event.key === 'Escape' && !searchPanel.hidden) {
          closeSearchResults();
          searchInput.focus();
        }
      });
      document.addEventListener('click', event => {
        if (!root.contains(event.target) && !searchPanel.contains(event.target)) closeSearchResults();
      });
    }

    /* ── Scroll-aware border ── */
    const scrollObs = () => {
      const announceH = parseInt(getComputedStyle(document.documentElement).getPropertyValue('--sbov3-announce-h')) || 0;
      root.classList.toggle('is-scrolled', window.scrollY > announceH + 4);
    };
    window.addEventListener('scroll', scrollObs, { passive: true });
    scrollObs();

    /* ── Tuck away past the hero ──
       On a page with the home hero, once the hero has scrolled up behind the
       header, scrolling down slides the header and the announcement pill out
       of view and any scroll back up brings them straight back. Above the
       hero's end they always show. They also stay while the shopper is using
       them: an open menu, search, drawer or locale list, or keyboard focus
       inside the header. The movement itself is CSS (.is-tucked in
       sbo-v3.css), a transform only, so nothing on the page reflows. */
    const hero = document.querySelector('main .sbov3hero');
    const announcement = document.querySelector('.sbov3ann');
    if (hero) {
      const TOLERANCE = 8;
      let lastY = window.scrollY;
      let tucked = false;
      let queued = false;

      const setTucked = value => {
        if (value === tucked) return;
        tucked = value;
        root.classList.toggle('is-tucked', value);
        if (announcement) announcement.classList.toggle('is-tucked', value);
      };

      const inUse = () =>
        root.contains(document.activeElement) ||
        Boolean(root.querySelector('.is-open, details[open]')) ||
        (searchPanel && !searchPanel.hidden) ||
        (drawer && drawer.classList.contains('is-open'));

      const update = () => {
        queued = false;
        const y = Math.max(0, window.scrollY);
        const maxY = document.documentElement.scrollHeight - window.innerHeight;
        /* iOS rubber-banding past the bottom reads as a scroll up; ignore it. */
        if (y > maxY) return;
        const styles = getComputedStyle(document.documentElement);
        const headerBottom = (parseFloat(styles.getPropertyValue('--sbov3-announce-h')) || 0) +
          (parseFloat(styles.getPropertyValue('--sbov3-header-h')) || 0);
        const pastHero = hero.getBoundingClientRect().bottom <= headerBottom;
        const delta = y - lastY;

        if (!pastHero || inUse()) setTucked(false);
        else if (delta > TOLERANCE) setTucked(true);
        else if (delta < -TOLERANCE) setTucked(false);

        if (!pastHero || Math.abs(delta) > TOLERANCE) lastY = y;
      };

      window.addEventListener('scroll', () => {
        if (queued) return;
        queued = true;
        requestAnimationFrame(update);
      }, { passive: true });

      /* Tabbing back into a tucked header shows it before focus lands. */
      root.addEventListener('focusin', () => setTucked(false));
    }

    /* ── Position below announcement bar ──
       Keeps the gap the stylesheet gives the floating bar (--sbov3-header-gap:
       10px at rest, 0 once scrolled); setting top to the bar height alone
       glued the header to the announcement. */
    function syncTop() {
      const h = getComputedStyle(document.documentElement).getPropertyValue('--sbov3-announce-h').trim() || '0px';
      root.style.top = `calc(${h} + var(--sbov3-header-gap, 10px))`;
    }
    syncTop();
    new MutationObserver(syncTop).observe(document.documentElement, { attributes: true, attributeFilter: ['style'] });

    /* ── Mobile drawer ── */
    /* The section wrappers in the header group are flex items of <body>, so
       their z-index applies even without positioning: the announcement and
       header wrappers each form a z-index:4 stacking context and paint in DOM
       order — trapping the drawer's z-40 under the announcement's z-30. While
       the drawer is open we raise its wrapper above the announcement. */
    const drawerSection = drawer ? drawer.closest('.shopify-section') : null;

    function openDrawer() {
      drawer.classList.add('is-open');
      overlay.classList.add('is-open');
      burger.setAttribute('aria-expanded', 'true');
      drawer.inert = false;
      if (drawerSection) drawerSection.classList.add('is-drawer-open');
      closeBtn && closeBtn.focus();
      document.body.style.overflow = 'hidden';
    }
    function closeDrawer() {
      drawer.classList.remove('is-open');
      overlay.classList.remove('is-open');
      burger.setAttribute('aria-expanded', 'false');
      drawer.inert = true;
      if (drawerSection) drawerSection.classList.remove('is-drawer-open');
      burger.focus();
      document.body.style.overflow = '';
    }
    if (burger) burger.addEventListener('click', openDrawer);
    if (closeBtn) closeBtn.addEventListener('click', closeDrawer);
    if (overlay) overlay.addEventListener('click', closeDrawer);
    document.addEventListener('keydown', e => { if (e.key === 'Escape' && drawer.classList.contains('is-open')) closeDrawer(); });

    /* Keep Tab focus inside the open drawer. */
    document.addEventListener('keydown', e => {
      if (e.key !== 'Tab' || !drawer.classList.contains('is-open')) return;
      const focusables = Array.from(drawer.querySelectorAll('a[href], button:not([disabled])'))
        .filter(el => el.offsetParent !== null && el.tabIndex !== -1 && !el.closest('[inert]'));
      if (!focusables.length) return;
      const first = focusables[0];
      const last = focusables[focusables.length - 1];
      if (e.shiftKey && document.activeElement === first) {
        e.preventDefault();
        last.focus();
      } else if (!e.shiftKey && document.activeElement === last) {
        e.preventDefault();
        first.focus();
      } else if (!drawer.contains(document.activeElement)) {
        e.preventDefault();
        first.focus();
      }
    });

    /* ── Cart count live update ── */
    let cartCountRequest = 0;

    function renderCartCount(count, shouldBump = true) {
      const normalizedCount = Number(count) || 0;
      root.querySelectorAll('[data-sbo3-cart-count]').forEach(el => {
        el.textContent = normalizedCount;
        el.classList.toggle('is-empty', normalizedCount === 0);
        el.setAttribute('aria-hidden', 'true');
        if (shouldBump) {
          el.classList.add('bump');
          setTimeout(() => el.classList.remove('bump'), 400);
        }
      });

      root.querySelectorAll('.sbov3header__cart').forEach(link => {
        link.setAttribute('aria-label', `Cart (${normalizedCount} ${normalizedCount === 1 ? 'item' : 'items'})`);
      });
    }

    async function refreshCartCount(event) {
      const eventCount = event?.detail?.cartData?.item_count;
      if (Number.isFinite(Number(eventCount))) {
        renderCartCount(eventCount);
        return;
      }

      const requestId = ++cartCountRequest;
      try {
        const response = await fetch('/cart.js', { headers: { Accept: 'application/json' } });
        if (!response.ok) return;
        const cartData = await response.json();
        if (requestId === cartCountRequest) renderCartCount(cartData.item_count);
      } catch {}
    }

    document.addEventListener('cart:updated', refreshCartCount);

    function subscribeToCartUpdates() {
      if (typeof subscribe !== 'function' || typeof PUB_SUB_EVENTS === 'undefined') return;
      subscribe(PUB_SUB_EVENTS.cartUpdate, event => {
        if (event?.cartData && Number.isFinite(Number(event.cartData.item_count))) {
          cartCountRequest += 1;
          renderCartCount(event.cartData.item_count);
        }
      });
    }

    if (document.readyState === 'loading') {
      document.addEventListener('DOMContentLoaded', subscribeToCartUpdates, { once: true });
    } else {
      subscribeToCartUpdates();
    }

    /* ── Country / currency switcher ── */
    document.querySelectorAll('[data-sbo3-locale]').forEach(widget => {
      const trigger   = widget.querySelector('[data-sbo3-locale-trigger]');
      const list      = widget.querySelector('.sbov3header__locale-list');
      const form      = widget.closest('form');
      const input     = form && form.querySelector('[data-sbo3-locale-input]');
      if (!trigger || !list || !form) return;

      function openList() {
        list.classList.add('is-open');
        trigger.setAttribute('aria-expanded', 'true');
      }
      function closeList() {
        list.classList.remove('is-open');
        trigger.setAttribute('aria-expanded', 'false');
      }
      function toggleList() {
        list.classList.contains('is-open') ? closeList() : openList();
      }

      trigger.addEventListener('click', (e) => { e.stopPropagation(); toggleList(); });
      document.addEventListener('click', (e) => {
        if (!widget.contains(e.target)) closeList();
      });
      document.addEventListener('keydown', (e) => {
        if (e.key === 'Escape') closeList();
      });

      widget.querySelectorAll('[data-sbo3-locale-opt]').forEach(btn => {
        btn.addEventListener('click', () => {
          const code = btn.dataset.sbo3LocaleOpt;
          if (input) input.value = code;
          closeList();
          document.dispatchEvent(new CustomEvent('sbo3:locale-change', {
            detail: { countryCode: code, currencyCode: btn.dataset.sbo3LocaleCurrency }
          }));
          form.submit();
        });
      });
    });

    /* ── Drawer locale buttons (separate forms) ── */
    document.querySelectorAll('[data-sbo3-drawer] [data-sbo3-locale-opt]').forEach(btn => {
      btn.addEventListener('click', () => {
        const form  = btn.closest('form');
        const input = form && form.querySelector('[data-sbo3-locale-input]');
        if (input) input.value = btn.dataset.sbo3LocaleOpt;
        document.dispatchEvent(new CustomEvent('sbo3:locale-change', {
          detail: { countryCode: btn.dataset.sbo3LocaleOpt, currencyCode: btn.dataset.sbo3LocaleCurrency }
        }));
        if (form) form.submit();
      });
    });

    /* ── Desktop dropdowns / flyouts (disclosure pattern) ──
       The top-level link stays a normal link; the caret is a separate toggle
       button. Panels stay inert (and closed) until the item gets .is-open —
       via the toggle, or via pointer hover as before. Keyboard users reach the
       hero without passing every hidden link. */
    root.querySelectorAll('.sbov3header__navitem.is-parent').forEach(navItem => {
      const flyout = navItem.querySelector('.sbov3header__flyout');
      const panel  = navItem.querySelector('.sbov3header__flyout, .sbov3header__dropdown');
      const toggle = navItem.querySelector('.sbov3header__nav-toggle');
      let closeTimer;
      const openFlyout = () => {
        window.clearTimeout(closeTimer);
        /* Only one panel may be open at a time. */
        root.querySelectorAll('.sbov3header__navitem.is-open').forEach(other => {
          if (other === navItem) return;
          other.classList.remove('is-open');
          const otherPanel = other.querySelector('.sbov3header__flyout, .sbov3header__dropdown');
          if (otherPanel) otherPanel.inert = true;
          const otherToggle = other.querySelector('.sbov3header__nav-toggle');
          if (otherToggle) otherToggle.setAttribute('aria-expanded', 'false');
        });
        navItem.classList.add('is-open');
        if (panel) panel.inert = false;
        if (toggle) toggle.setAttribute('aria-expanded', 'true');
      };
      const closeFlyout = () => {
        window.clearTimeout(closeTimer);
        navItem.classList.remove('is-open');
        if (panel) panel.inert = true;
        if (toggle) toggle.setAttribute('aria-expanded', 'false');
      };
      const scheduleClose = () => {
        window.clearTimeout(closeTimer);
        closeTimer = window.setTimeout(closeFlyout, 180);
      };
      /* Hover is for mice only: a tap fires pointerenter and then click, so
         a touch-driven open would be undone by the toggle's click at once. */
      navItem.addEventListener('pointerenter', event => { if (event.pointerType === 'mouse') openFlyout(); });
      navItem.addEventListener('pointerleave', event => { if (event.pointerType === 'mouse') scheduleClose(); });
      if (toggle) {
        toggle.addEventListener('click', () => {
          navItem.classList.contains('is-open') ? closeFlyout() : openFlyout();
        });
        toggle.addEventListener('keydown', event => {
          if (event.key === 'ArrowDown' && panel) {
            event.preventDefault();
            openFlyout();
            const first = panel.querySelector('a[href], button');
            if (first) first.focus();
          }
        });
      }
      navItem.addEventListener('keydown', event => {
        if (event.key === 'Escape' && navItem.classList.contains('is-open')) {
          event.stopPropagation();
          closeFlyout();
          if (toggle) toggle.focus();
        }
      });
      navItem.addEventListener('focusout', event => {
        if (!navItem.contains(event.relatedTarget)) closeFlyout();
      });
      document.addEventListener('pointerdown', event => {
        if (!navItem.contains(event.target)) closeFlyout();
      });
      if (!flyout) return;
      const triggers = flyout.querySelectorAll('.sbov3header__flyout-item.has-sub[data-fi]');
      const panels   = flyout.querySelectorAll('.sbov3header__flyout-panel[data-fp]');

      function activatePanel(idx) {
        triggers.forEach(t => t.classList.toggle('is-hover', t.dataset.fi === idx));
        panels.forEach(p => p.classList.toggle('is-active', p.dataset.fp === idx));
      }

      triggers.forEach(trigger => {
        trigger.addEventListener('mouseenter', () => activatePanel(trigger.dataset.fi));
        trigger.addEventListener('focus', () => activatePanel(trigger.dataset.fi));
      });
    });

    /* ── Drawer accordion: expand/collapse L2 + L3 ──
       A submenu animates max-height from 0 to its real content height. When a
       nested group opens, its already-open ancestors are uncapped to `none`
       so the growing child is never clipped, at any depth — the old fixed
       800px / 600px caps were what hid brands like PCA, EltaMD and Vivier. */
    if (drawer) {
      const reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;

      const openSub = sub => {
        /* Uncap every already-open ancestor first. They are fully expanded and
           static, so switching them to `none` is invisible — but it means the
           submenu about to grow inside them is never clipped, however deep. */
        let p = sub.parentElement;
        while (p && p !== drawer) {
          if (p.classList.contains('sbov3header__drawer-sub') && p.classList.contains('is-open')) {
            p.style.maxHeight = 'none';
          }
          p = p.parentElement;
        }
        sub.classList.add('is-open');
        sub.inert = false;
        if (reduceMotion) { sub.style.maxHeight = 'none'; return; }
        sub.style.maxHeight = sub.scrollHeight + 'px';
      };

      const closeSub = sub => {
        /* Collapse nested groups first so their buttons reset with the branch. */
        sub.querySelectorAll('.sbov3header__drawer-sub.is-open').forEach(d => {
          d.classList.remove('is-open');
          d.inert = true;
          d.style.maxHeight = '0px';
        });
        sub.querySelectorAll('[data-sbo3-expand][aria-expanded="true"]')
          .forEach(b => b.setAttribute('aria-expanded', 'false'));
        sub.classList.remove('is-open');
        sub.inert = true;
        if (reduceMotion) { sub.style.maxHeight = '0px'; return; }
        /* Pin the current (possibly uncapped) height, then animate to 0. */
        sub.style.maxHeight = sub.scrollHeight + 'px';
        sub.getBoundingClientRect();
        requestAnimationFrame(() => { sub.style.maxHeight = '0px'; });
      };

      drawer.querySelectorAll('[data-sbo3-expand]').forEach(btn => {
        btn.addEventListener('click', function () {
          const item = btn.parentElement.parentElement;
          const sub  = item.querySelector(':scope > .sbov3header__drawer-sub');
          if (!sub) return;
          const willOpen = !sub.classList.contains('is-open');
          btn.setAttribute('aria-expanded', willOpen ? 'true' : 'false');
          willOpen ? openSub(sub) : closeSub(sub);
        });
      });
    }
  })();
